import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';

// ============================================================================
// HIRING SPOT ENUMS AND TYPES
// ============================================================================

/**
 * Hiring role levels
 */
export type HiringRole =
  | 'Specialist'
  | 'Lead'
  | 'Strategist'
  | 'Manager';

/**
 * Confidence levels for hire success
 */
export type ConfidenceLevel =
  | 'High'
  | 'Medium'
  | 'Low';

/**
 * Entry tracking a previous confidence level value for audit history
 */
export interface HiringConfidenceHistoryEntry {
  previous_value: ConfidenceLevel;
  changed_at: Timestamp;
  changed_by: string;
}

/**
 * Priority levels for hiring spots
 */
export type PriorityLevel =
  | 'high'
  | 'medium'
  | 'low';

/**
 * Channel background preferences
 */
export type ChannelBackground =
  | 'Search'
  | 'Social'
  | 'Programmatic'
  | 'Mixed';

/**
 * Vertical/industry background preferences
 */
export type VerticalBackground =
  | 'Ecommerce'
  | 'Lead Gen'
  | 'No Preference';

/**
 * Location/timezone preferences
 */
export type LocationPreference =
  | 'PST'
  | 'EST'
  | 'No Preference (Canada)'
  | 'NZ';

/**
 * Business impact areas
 */
export type ImpactArea =
  | 'New Project Growth'
  | 'Existing Project Growth & Retention'
  | 'Team Retention'
  | 'Team Growth';

/**
 * Approval status for hiring spots
 *
 * Flow: pending_approval -> approved -> filled
 * Archive is independent and can happen at any stage
 */
export type ApprovalStatus =
  | 'pending_approval'
  | 'approved'
  | 'filled';

/**
 * Conversation message types
 */
export type ConversationMessageType =
  | 'comment'
  | 'status_change'
  | 'approval_request';

// ============================================================================
// SUPPORTING INTERFACES
// ============================================================================

/**
 * Individual conversation/activity feed entry
 * Follows the same pattern as workflow_spots
 */
export interface ConversationEntry {
  id: string;
  author_id: string;
  author_name: string;
  author_avatar_url?: string;
  message: string;
  timestamp: Timestamp;
  message_type: ConversationMessageType;
  metadata?: {
    previous_status?: ApprovalStatus;
    new_status?: ApprovalStatus;
    mentioned_users?: string[];
  };
}

/**
 * Approval history entry
 */
export interface ApprovalHistoryEntry {
  timestamp: Timestamp;
  action: 'submitted' | 'approved' | 'filled' | 'archived' | 'unarchived' | 'closed' | 'reopened';
  actor_id: string;
  actor_name: string;
  notes?: string;
}

/**
 * Approval metadata for hiring spots
 */
export interface ApprovalMetadata {
  submitted_at?: Timestamp;
  submitted_by?: string;
  submitted_by_name?: string;
  reviewed_at?: Timestamp;
  reviewed_by?: string;
  reviewed_by_name?: string;
  review_notes?: string;
  approval_history: ApprovalHistoryEntry[];
}

/**
 * Structured notes for hiring context
 */
export interface HiringSpotNotes {
  // Context questions
  team_strengths: string | null;
  desired_skills: string | null;
  workflow_responsibilities: string | null;
  team_planning_impact: string | null;
  additional_context: string | null;

  // Activity feed
  conversation_history: ConversationEntry[];
  last_updated: Timestamp;
  last_updated_by: string;
  last_updated_by_name: string;
}

// ============================================================================
// MAIN HIRING SPOT INTERFACE
// ============================================================================

/**
 * Complete hiring spot document structure
 * Collection: hiring_spots
 */
export interface HiringSpot {
  // Document identification
  id: string;

  // Core hiring information
  hiring_manager: string;              // user_id from system_users
  trainer: string | null;              // user_id from system_users
  collaborators: string[];             // array of user_ids
  workflows: string[];                 // array of workflow IDs

  // Role and team context
  role: HiringRole;
  team_id: string;                     // from workflow_team_teams
  team_name: string;                   // Stored for activity feed context
  program_id: string;                  // from workflow_team_programs
  program_name: string;                // Stored for activity feed context
  department: string;                  // Department (e.g., "Performance")

  // Hiring criteria
  confidence: ConfidenceLevel;
  confidence_history?: HiringConfidenceHistoryEntry[];
  priority: PriorityLevel;
  channel_background: ChannelBackground[];
  vertical_background: VerticalBackground;
  location: LocationPreference;
  impact: ImpactArea[];
  available_start_date: string | null; // ISO date string - requested start date from team
  forecasted_start_date: string | null; // ISO date string - adjusted by Talent Acquisition

  // Placement relationship
  confirmed_hire_id: string | null; // Link to paired confirmed hire from hiring_confirmed_hires

  // Context notes
  notes: HiringSpotNotes;

  // Approval workflow
  approval_status: ApprovalStatus;
  is_archived: boolean;
  // Closed = filled and manually retired from active planning views (Workflow
  // Summary headcount, team panels, Timing, Forecast). Distinct from archived:
  // a closed spot REMAINS in the Hiring Placement board as the historical
  // placement record. Optional because existing docs predate the field and
  // read as falsy ("not closed"); set explicitly on create and via close/reopen.
  is_closed?: boolean;
  approval_metadata: ApprovalMetadata;

  // Standard audit fields
  created_at: Timestamp;
  updated_at: Timestamp;
  created_by: string;
  created_by_name: string;
  last_modified_by: string;
  last_modified_by_name: string;
  version: number;

  // Optional metadata
  metadata?: {
    description?: string;
    tags?: string[];
  };
}

// ============================================================================
// CRUD OPERATION INTERFACES
// ============================================================================

/**
 * Data structure for creating a new hiring spot
 */
export interface CreateHiringSpotData {
  // Required fields
  hiring_manager: string;
  role: HiringRole;
  team_id: string;
  team_name: string;
  program_id: string;
  program_name: string;
  department: string;
  confidence: ConfidenceLevel;
  priority: PriorityLevel;
  channel_background: ChannelBackground[];
  vertical_background: VerticalBackground;
  location: LocationPreference;
  impact: ImpactArea[];

  // Optional fields
  trainer?: string | null;
  collaborators?: string[];
  workflows?: string[];
  available_start_date?: string | null;
  forecasted_start_date?: string | null;

  // Optional notes
  team_strengths?: string | null;
  desired_skills?: string | null;
  workflow_responsibilities?: string | null;
  team_planning_impact?: string | null;
  additional_context?: string | null;

  // Optional metadata
  metadata?: {
    description?: string;
    tags?: string[];
  };
}

/**
 * Data structure for updating a hiring spot
 */
export interface UpdateHiringSpotData {
  // All fields optional for partial updates
  hiring_manager?: string;
  trainer?: string | null;
  collaborators?: string[];
  workflows?: string[];
  role?: HiringRole;
  department?: string;
  confidence?: ConfidenceLevel;
  priority?: PriorityLevel;
  channel_background?: ChannelBackground[];
  vertical_background?: VerticalBackground;
  location?: LocationPreference;
  impact?: ImpactArea[];
  available_start_date?: string | null;
  forecasted_start_date?: string | null;
  metadata?: {
    description?: string;
    tags?: string[];
  };
  // Context notes fields (mapped to notes sub-object on save)
  team_strengths?: string | null;
  desired_skills?: string | null;
  workflow_responsibilities?: string | null;
  team_planning_impact?: string | null;
  additional_context?: string | null;
}

/**
 * Data structure for adding a conversation entry
 */
export interface AddConversationEntryData {
  message: string;
  message_type: ConversationMessageType;
  metadata?: {
    previous_status?: ApprovalStatus;
    new_status?: ApprovalStatus;
    mentioned_users?: string[];
  };
}

// ============================================================================
// ZOD VALIDATION SCHEMAS
// ============================================================================

/**
 * Hiring role validation
 */
const HiringRoleSchema = z.enum([
  'Specialist',
  'Lead',
  'Strategist',
  'Manager'
]);

/**
 * Confidence level validation
 */
const ConfidenceLevelSchema = z.enum([
  'High',
  'Medium',
  'Low'
]);

/**
 * Priority level validation
 */
const PriorityLevelSchema = z.enum([
  'high',
  'medium',
  'low'
]);

/**
 * Channel background validation
 */
const ChannelBackgroundSchema = z.enum([
  'Search',
  'Social',
  'Programmatic',
  'Mixed'
]);

/**
 * Vertical background validation
 */
const VerticalBackgroundSchema = z.enum([
  'Ecommerce',
  'Lead Gen',
  'No Preference'
]);

/**
 * Location preference validation
 */
const LocationPreferenceSchema = z.enum([
  'PST',
  'EST',
  'No Preference (Canada)',
  'NZ'
]);

/**
 * Impact area validation
 */
const ImpactAreaSchema = z.enum([
  'New Project Growth',
  'Existing Project Growth & Retention',
  'Team Retention',
  'Team Growth'
]);

/**
 * Approval status validation
 */
const ApprovalStatusSchema = z.enum([
  'pending_approval',
  'approved',
  'filled'
]);

/**
 * Conversation message type validation
 */
const ConversationMessageTypeSchema = z.enum([
  'comment',
  'status_change',
  'approval_request'
]);

/**
 * User ID validation (supports employee IDs)
 */
const UserIdSchema = z.string().min(1).max(128);

/**
 * Create hiring spot validation
 */
export const CreateHiringSpotDataSchema = z.object({
  // Required fields
  hiring_manager: UserIdSchema,
  role: HiringRoleSchema,
  team_id: z.string().min(1),
  team_name: z.string().min(1).max(200),
  program_id: z.string().min(1),
  program_name: z.string().min(1).max(200),
  department: z.string().min(1).max(100),
  confidence: ConfidenceLevelSchema,
  priority: PriorityLevelSchema,
  channel_background: z.array(ChannelBackgroundSchema).min(1),
  vertical_background: VerticalBackgroundSchema,
  location: LocationPreferenceSchema,
  impact: z.array(ImpactAreaSchema).min(1),

  // Optional fields
  trainer: UserIdSchema.nullable().optional(),
  collaborators: z.array(UserIdSchema).optional(),
  workflows: z.array(z.string()).optional(),
  available_start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  forecasted_start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),

  // Optional notes
  team_strengths: z.string().max(5000).nullable().optional(),
  desired_skills: z.string().max(5000).nullable().optional(),
  workflow_responsibilities: z.string().max(5000).nullable().optional(),
  team_planning_impact: z.string().max(5000).nullable().optional(),
  additional_context: z.string().max(5000).nullable().optional(),

  // Optional metadata
  metadata: z.object({
    description: z.string().max(500).optional(),
    tags: z.array(z.string().max(50)).max(10).optional(),
  }).optional(),
});

/**
 * Update hiring spot validation
 */
export const UpdateHiringSpotDataSchema = z.object({
  hiring_manager: UserIdSchema.optional(),
  trainer: UserIdSchema.nullable().optional(),
  collaborators: z.array(UserIdSchema).optional(),
  workflows: z.array(z.string()).optional(),
  role: HiringRoleSchema.optional(),
  department: z.string().min(1).max(100).optional(),
  confidence: ConfidenceLevelSchema.optional(),
  priority: PriorityLevelSchema.optional(),
  channel_background: z.array(ChannelBackgroundSchema).min(1).optional(),
  vertical_background: VerticalBackgroundSchema.optional(),
  location: LocationPreferenceSchema.optional(),
  impact: z.array(ImpactAreaSchema).min(1).optional(),
  available_start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  forecasted_start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  metadata: z.object({
    description: z.string().max(500).optional(),
    tags: z.array(z.string().max(50)).max(10).optional(),
  }).optional(),
  // Context notes fields (mapped to notes sub-object on save)
  team_strengths: z.string().max(5000).nullable().optional(),
  desired_skills: z.string().max(5000).nullable().optional(),
  workflow_responsibilities: z.string().max(5000).nullable().optional(),
  team_planning_impact: z.string().max(5000).nullable().optional(),
  additional_context: z.string().max(5000).nullable().optional(),
});

/**
 * Add conversation entry validation
 */
export const AddConversationEntryDataSchema = z.object({
  message: z.string().min(1).max(5000),
  message_type: ConversationMessageTypeSchema,
  metadata: z.object({
    previous_status: ApprovalStatusSchema.optional(),
    new_status: ApprovalStatusSchema.optional(),
    mentioned_users: z.array(UserIdSchema).optional(),
  }).optional(),
});
